import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deletePage, archivePage, duplicatePage, updatePageFromSocket, removePage, addPage, permanentDeletePage, restorePage, updatePageRestoreOptimistic } from '../../redux/Slices/pageSlice';
import { savePageAsTemplate } from '../../redux/Slices/templateSlice';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import {
  Delete,
  Archive,
  Unarchive,
  ContentCopy,
  Share,
  Lock,
  Public as PublicIcon,
  DriveFileMove,
  Undo
} from '../icons';
import './scss/PageActionsMenu.scss';

const PageActionsMenu = ({ page, workspaceId, onMove, onDeleteSuccess, onDuplicateSuccess, isOwner, onDuplicateOptimistic, onDuplicateFinalize, isTrash }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handlePopoverClick } = useHandlePopoverClick();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const closePopover = () => {
    dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    // Optimistic removal
    const originalPage = { ...page };
    try {
      setLoading(true);
      if (!page?.shortId) throw new Error('Invalid page data');

      await dispatch(deletePage({ pageId: page.shortId })).unwrap();
      showSuccess(dispatch, 'Page deleted successfully');
      // Navigate based on context or let parent handle it
      if (workspaceId) {
        navigate(`/w/${workspaceId}/pages`);
      } else if (typeof onDeleteSuccess === 'function') {
        onDeleteSuccess();
      } else {
        navigate(-1);
      }
      closePopover();
    } catch (error) {
      // Revert optimistic removal
      const workspace = originalPage.workspace || originalPage.workspaceId || null;
      dispatch(addPage({ ...originalPage, workspace }));
      console.error('Failed to delete page:', error);
      showError(dispatch, 'Failed to delete page: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    try {
      setLoading(true);
      if (!page?.shortId) throw new Error('Invalid page data');
      
      await dispatch(permanentDeletePage({ pageId: page.shortId })).unwrap();
      showSuccess(dispatch, 'Page permanently deleted');
      
      if (typeof onDeleteSuccess === 'function') {
        onDeleteSuccess();
      } else if (workspaceId) {
        navigate(`/w/${workspaceId}/pages`);
      } else {
        navigate(-1);
      }
      closePopover();
    } catch (error) {
      console.error('Failed to permanently delete page:', error);
      showError(dispatch, 'Failed to delete permanently: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    try {
      setLoading(true);
      const isArchiving = !page.archived;
      if (!page?.shortId) throw new Error('Invalid page data');
      
      await dispatch(archivePage({
        pageId: page.shortId,
        archived: isArchiving
      })).unwrap();
      
      // Optimistic update after success
      dispatch(updatePageFromSocket({ ...page, archived: isArchiving }));
      showSuccess(dispatch, isArchiving ? 'Page archived successfully' : 'Page unarchived successfully');
      closePopover();
    } catch (error) {
      console.error('Failed to archive page:', error);
      // Revert optimistic change
      dispatch(updatePageFromSocket({ ...page, archived: !(!page.archived) }));
      showError(dispatch, 'Failed to archive page: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async () => {
    let tempId = null;
    try {
      setLoading(true);
      if (!page?.shortId) throw new Error('Invalid page data');
      if (typeof onDuplicateOptimistic === 'function') {
        tempId = onDuplicateOptimistic(page);
      }
      const result = await dispatch(duplicatePage({ pageId: page.shortId })).unwrap();
      if (tempId && typeof onDuplicateFinalize === 'function') {
        onDuplicateFinalize(tempId, result, true);
      }
      // Navigate to the duplicated page
      if (workspaceId) {
        navigate(`/w/${workspaceId}/pages/${result.shortId}`);
      } else {
        navigate(`/p/${result.shortId}`);
      }
      if (typeof onDuplicateSuccess === 'function') {
        onDuplicateSuccess(result);
      }
      closePopover();
      showSuccess(dispatch, 'Page duplicated successfully');
    } catch (error) {
      if (tempId && typeof onDuplicateFinalize === 'function') {
        onDuplicateFinalize(tempId, null, false);
      }
      console.error('Failed to duplicate page:', error);
      showError(dispatch, 'Failed to duplicate page: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    if (!page) {
      showError(dispatch, 'Invalid page data');
      return;
    }
    const currentTarget = e.currentTarget;
    // Close current popover and open Share members popover
    closePopover();
    setTimeout(() => {
      handlePopoverClick(currentTarget, 'sharePageMembers', {
        page,
        isOwner: !!isOwner,
        isClosed: true
      });
    }, 50);
  };

  const handleRestore = async () => {
    try {
      setLoading(true);
      if (!page?.shortId) throw new Error('Invalid page data');
      
      await dispatch(restorePage({ pageId: page.shortId })).unwrap();
      dispatch(updatePageRestoreOptimistic({ pageId: page.shortId }));
      showSuccess(dispatch, 'Page restored successfully');
      closePopover();
    } catch (error) {
      console.error('Failed to restore page:', error);
      showError(dispatch, 'Failed to restore page: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    try {
      setLoading(true);
      if (!page?._id && !page?.shortId) throw new Error('Invalid page data');
      await dispatch(savePageAsTemplate({
        pageId: page._id || page.shortId,
        name: page.title || 'Untitled Template',
        category: 'general',
      })).unwrap();
      showSuccess(dispatch, 'Page saved as template');
      closePopover();
    } catch (error) {
      console.error('Failed to save as template:', error);
      showError(dispatch, 'Failed to save as template');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeVisibility = (e) => {
    e.stopPropagation();
    if (!page) {
      showError(dispatch, 'Invalid page data');
      return;
    }

    const currentTarget = e.currentTarget;
    // Close current popover and open visibility menu
    closePopover();

    // Small delay to ensure the previous popover is closed
    setTimeout(() => {
      handlePopoverClick(currentTarget, 'pageVisibility', {
        page,
        workspaceId,
        isClosed: false
      });
    }, 50);
  };

  const getVisibilityIcon = () => {
    if (typeof page?.visibility !== 'undefined') {
      switch (page.visibility) {
        case 'Private':
          return <Lock />;
        case 'Public':
          return <PublicIcon />;
        default:
          return <Share />;
      }
    }
    // Fallback for pages without visibility field
    return page?.isPublic ? <PublicIcon /> : <Lock />;
  };

  return (
    <div className="page-actions-menu">
      {!showDeleteConfirm ? (
        <>
          {onMove && (
            <button
              className="page-action-item"
              onClick={() => { closePopover(); onMove(); }}
              disabled={loading}
              type="button"
            >
              <DriveFileMove />
              <span>Move page</span>
            </button>
          )}

          {isTrash ? (
            <button
              className="page-action-item"
              onClick={handleRestore}
              disabled={loading}
              type="button"
            >
              <Undo />
              <span>Restore</span>
            </button>
          ) : (
            <button
              className="page-action-item"
              onClick={handleDuplicate}
              disabled={loading}
              type="button"
            >
              <ContentCopy />
              <span>Duplicate</span>
            </button>
          )}

          <button
            className="page-action-item"
            onClick={handleArchive}
            disabled={loading}
          >
            {page.archived ? <Unarchive /> : <Archive />}
            <span>{page.archived ? 'Unarchive' : 'Archive'}</span>
          </button>

          {workspaceId && typeof page?.visibility !== 'undefined' && (
            <button
              className="page-action-item"
              onClick={handleChangeVisibility}
              disabled={loading}
            >
              {getVisibilityIcon()}
              <span>Change visibility{page.visibility ? ` (${page.visibility})` : ''}</span>
            </button>
          )}

          <button
            className="page-action-item"
            onClick={(e) => handleShare(e)}
            disabled={loading}
            type="button"
          >
            <Share />
            <span>Share</span>
          </button>

          {!isTrash && (
            <button
              className="page-action-item"
              onClick={handleSaveAsTemplate}
              disabled={loading}
              type="button"
            >
              <ContentCopy />
              <span>Save as template</span>
            </button>
          )}

          <div className="page-actions-divider" />

          <button
            className="page-action-item page-action-delete"
            onClick={isTrash ? handlePermanentDelete : handleDelete}
            disabled={loading}
          >
            <Delete />
            <span>{isTrash ? 'Delete permanently' : 'Delete'}</span>
          </button>
        </>
      ) : (
        <div className="page-delete-confirm">
          <p>Are you sure you want to {isTrash ? 'permanently delete' : 'delete'} this page?</p>
          <p className="page-delete-warning">This will also {isTrash ? 'permanently' : ''} delete all sub-pages.</p>
          <div className="page-delete-actions">
            <button
              className="page-delete-cancel"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="page-delete-confirm-btn"
              onClick={isTrash ? handlePermanentDelete : handleDelete}
              disabled={loading}
            >
              {loading ? (isTrash ? 'Deleting...' : 'Deleting...') : (isTrash ? 'Delete Permanently' : 'Delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageActionsMenu;

